import React from "react";
import { createRoot } from "react-dom/client";
import RentChartEmbed from "./RentChartEmbed";

interface ChartConfig {
  rentValue?: number;
  cropName?: string;
}

// Type declarations for the global namespace
declare global {
  interface Window {
    RentCurvesChart: {
      init: (elementId: string, config?: ChartConfig) => void;
      update: (rentValue: number, cropName: string) => void;
      unmount: (elementId: string) => void;
    };
  }
}

// Track mounted instances
const mountedInstances = new Map<string, ReturnType<typeof createRoot>>();

// Initialize chart with config object
function init(elementId: string, config?: ChartConfig) {
  const container = document.getElementById(elementId);
  if (!container) {
    console.error(`RentCurvesChart: Element with id "${elementId}" not found`);
    return;
  }

  // Prevent double-mounting
  if (mountedInstances.has(elementId)) {
    console.warn(`RentCurvesChart: "${elementId}" already mounted. Use unmount() first to re-initialize.`);
    return;
  }

  const root = createRoot(container);
  mountedInstances.set(elementId, root);
  root.render(
    React.createElement(RentChartEmbed, {
      initialRentValue: config?.rentValue,
      initialCropName: config?.cropName,
    })
  );
}

// Manually push data to chart via custom event
function update(rentValue: number, cropName: string) {
  window.dispatchEvent(
    new CustomEvent("rentcurves:calculated", {
      detail: { rentValue, cropName, isLowYield: false, state: "" },
    })
  );
}

// Unmount a chart instance
function unmount(elementId: string) {
  const root = mountedInstances.get(elementId);
  if (root) {
    root.unmount();
    mountedInstances.delete(elementId);
  }
}

// Expose global API
window.RentCurvesChart = {
  init,
  update,
  unmount,
};

// Auto-initialize elements with data-rentcurves-chart attribute
document.addEventListener("DOMContentLoaded", () => {
  const autoInitElements = document.querySelectorAll("[data-rentcurves-chart]");
  autoInitElements.forEach((element) => {
    if (element.id) {
      init(element.id);
    } else {
      const id = `rentcurves-chart-${Math.random().toString(36).substr(2, 9)}`;
      element.id = id;
      init(id);
    }
  });
});

export { init, update, unmount };
